/**
 * Exchange-grade design tokens — single source of truth.
 *
 * Every theming surface in the app derives from this file:
 *   - CSS variables on `:root` / `.dark`  via `tokensToCssVarMap()`
 *   - Tailwind `theme.extend.colors`       via `tailwindColors()`
 *   - MUI palette + typography             via `tokensToMuiPalette()`
 *
 * The flow is one-way: tokens.ts → CSS vars + tailwind config + MUI theme.
 * Never hardcode a hex in a component; if a value is missing, add it here
 * and let the mirrors regenerate. The drift test in tokens.test.ts asserts
 * every mode exposes the same key shape so a missing dark/light variant
 * fails the build.
 *
 * Visual direction: dark-first institutional terminal. Near-black surfaces
 * with a faint blue undertone, tabular-numeric typography, low glare.
 * Electric blue accent (#3b82f6) replaces the prior purple. Direction
 * green/red are aligned with the existing TradeForm to avoid e2e churn;
 * status colors are intentionally distinct from direction so a "down"
 * health pill never reads as a sell button.
 */

// -----------------------------------------------------------------------------
// Primitive palette — raw values; components MUST NOT import these directly.
// They exist so the semantic layer below has a controlled vocabulary.
// -----------------------------------------------------------------------------

const palette = {
  // Cool-neutral ramp for dark surfaces and text. Slight blue undertone reads
  // as "terminal" without going into purple territory.
  ink: {
    950: '#0a0c11',
    900: '#0b0d12', // page bg
    850: '#0f131c',
    800: '#13171f', // panel
    750: '#171c27',
    700: '#1a1f2b', // elevated
    650: '#1e2532',
    600: '#262d3c', // border subtle
    500: '#384154', // border default
    400: '#4a546b', // border strong
    300: '#6b7488', // text tertiary
    200: '#9aa3b2', // text secondary
    100: '#c1c7d2',
    50: '#e6e9ef', // text primary
    25: '#f3f5f8',
  },
  // Warm-neutral ramp for light mode. The light values keep '#ffffff' /
  // '#e5e7eb' anchors that the AccountSummary / RightSidebar style tests
  // pin — the migration is token-driven but visually conservative in light
  // mode to avoid suite churn.
  paper: {
    0: '#ffffff',
    50: '#f6f7fb',
    100: '#eef0f6',
    150: '#e9ebf2',
    200: '#e5e7eb', // border subtle (light)
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  // Electric blue — accent. Also the existing active-tab underline color in
  // RightSidebar (#3b82f6), so this is a no-op visual move at the
  // selected-state level and a brand shift away from purple everywhere else.
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // primary accent
    600: '#2563eb',
    700: '#1d4ed8',
  },
  // Trading direction — green/red. Aligned with TradeForm.tsx (#10b981 /
  // #ef4444) so the e2e suite's submit-button selectors and visual state
  // assertions stay green.
  emerald: {
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    bg: 'rgba(16, 185, 129, 0.10)',
    bgStrong: 'rgba(16, 185, 129, 0.18)',
  },
  rose: {
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    bg: 'rgba(239, 68, 68, 0.10)',
    bgStrong: 'rgba(239, 68, 68, 0.18)',
  },
  // Status — kept distinct from direction. Amber for warn, red for error
  // (deeper than direction sell), cyan for info, slate for neutral.
  amber: { 500: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  statusRed: { 500: '#dc2626', bg: 'rgba(220, 38, 38, 0.12)' },
  cyan: { 500: '#06b6d4', bg: 'rgba(6, 182, 212, 0.12)' },
  slate: { 500: '#64748b', bg: 'rgba(100, 116, 139, 0.12)' },
} as const;

// -----------------------------------------------------------------------------
// Semantic token shape. Both `dark` and `light` MUST expose the same keys;
// the drift test enforces this. Components only ever read semantic tokens.
// -----------------------------------------------------------------------------

export interface ColorTokens {
  bg: {
    base: string; // app background
    raised: string; // top of base, e.g. headers on bg
    sunken: string; // recessed area (input rows, code blocks)
    overlay: string; // modal/scrim
  };
  surface: {
    panel: string; // primary card / panel surface
    elevated: string; // popovers, dropdowns
    hover: string; // hover state for rows/buttons on panels
    pressed: string; // active/pressed state
  };
  border: {
    subtle: string; // hairlines between rows
    default: string; // panel borders
    strong: string; // emphasized borders
    focus: string; // focus ring color
  };
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string; // text on accent fills
    disabled: string;
    link: string;
    danger: string;
    warning: string;
    success: string;
  };
  direction: {
    buy: string;
    buyMuted: string;
    buyBg: string;
    sell: string;
    sellMuted: string;
    sellBg: string;
    flat: string;
  };
  status: {
    ok: string;
    okBg: string;
    warn: string;
    warnBg: string;
    error: string;
    errorBg: string;
    info: string;
    infoBg: string;
    neutral: string;
    neutralBg: string;
  };
  accent: {
    primary: string;
    primaryHover: string;
    primaryMuted: string;
    primarySubtle: string;
  };
  flash: {
    up: string; // market-data tick-up highlight
    down: string;
  };
}

const darkColors: ColorTokens = {
  bg: {
    base: palette.ink[900],
    raised: palette.ink[850],
    sunken: palette.ink[950],
    overlay: 'rgba(7, 9, 14, 0.72)',
  },
  surface: {
    panel: palette.ink[800],
    elevated: palette.ink[700],
    hover: palette.ink[750],
    pressed: palette.ink[650],
  },
  border: {
    subtle: palette.ink[600],
    default: palette.ink[500],
    strong: palette.ink[400],
    focus: palette.blue[500],
  },
  text: {
    primary: palette.ink[50],
    secondary: palette.ink[200],
    tertiary: palette.ink[300],
    inverse: palette.ink[900],
    disabled: palette.ink[400],
    link: palette.blue[400],
    danger: palette.rose[400],
    warning: palette.amber[500],
    success: palette.emerald[400],
  },
  direction: {
    buy: palette.emerald[500],
    buyMuted: palette.emerald[400],
    buyBg: palette.emerald.bg,
    sell: palette.rose[500],
    sellMuted: palette.rose[400],
    sellBg: palette.rose.bg,
    flat: palette.ink[300],
  },
  status: {
    ok: palette.emerald[500],
    okBg: palette.emerald.bg,
    warn: palette.amber[500],
    warnBg: palette.amber.bg,
    error: palette.statusRed[500],
    errorBg: palette.statusRed.bg,
    info: palette.cyan[500],
    infoBg: palette.cyan.bg,
    neutral: palette.slate[500],
    neutralBg: palette.slate.bg,
  },
  accent: {
    primary: palette.blue[500],
    primaryHover: palette.blue[400],
    primaryMuted: palette.blue[700],
    primarySubtle: 'rgba(59, 130, 246, 0.14)',
  },
  flash: {
    up: 'rgba(16, 185, 129, 0.22)',
    down: 'rgba(239, 68, 68, 0.22)',
  },
};

const lightColors: ColorTokens = {
  bg: {
    base: palette.paper[50],
    raised: palette.paper[0],
    sunken: palette.paper[100],
    overlay: 'rgba(17, 24, 39, 0.32)',
  },
  surface: {
    // White panel is the literal anchor that AccountSummary /
    // RightSidebar style tests pin (`backgroundColor: 'white'`); keep it.
    panel: palette.paper[0],
    elevated: palette.paper[0],
    hover: palette.paper[100],
    pressed: palette.paper[150],
  },
  border: {
    // #e5e7eb is the literal anchor pinned by existing style tests; keep it.
    subtle: palette.paper[200],
    default: palette.paper[300],
    strong: palette.paper[400],
    focus: palette.blue[600],
  },
  text: {
    primary: palette.paper[900],
    secondary: palette.paper[600],
    tertiary: palette.paper[500],
    inverse: palette.paper[0],
    disabled: palette.paper[400],
    link: palette.blue[600],
    danger: palette.rose[600],
    warning: palette.amber[500],
    success: palette.emerald[600],
  },
  direction: {
    buy: palette.emerald[500],
    buyMuted: palette.emerald[600],
    buyBg: palette.emerald.bg,
    sell: palette.rose[500],
    sellMuted: palette.rose[600],
    sellBg: palette.rose.bg,
    flat: palette.paper[500],
  },
  status: {
    ok: palette.emerald[600],
    okBg: palette.emerald.bg,
    warn: palette.amber[500],
    warnBg: palette.amber.bg,
    error: palette.statusRed[500],
    errorBg: palette.statusRed.bg,
    info: palette.cyan[500],
    infoBg: palette.cyan.bg,
    neutral: palette.slate[500],
    neutralBg: palette.slate.bg,
  },
  accent: {
    primary: palette.blue[600],
    primaryHover: palette.blue[700],
    primaryMuted: palette.blue[300],
    primarySubtle: 'rgba(37, 99, 235, 0.10)',
  },
  flash: {
    up: 'rgba(16, 185, 129, 0.18)',
    down: 'rgba(239, 68, 68, 0.18)',
  },
};

// -----------------------------------------------------------------------------
// Non-color tokens — single set, mode-independent.
// -----------------------------------------------------------------------------

export const typography = {
  fontFamily: {
    // Headings + body. Inter is already linked in the host page; keep the
    // generic fallback so we never blank the UI if the network drops it.
    sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    // Numeric stack — system mono with `tabular-nums` applied at the
    // utility level (see NumericText). Mono ensures order book / blotter
    // numerics stay column-aligned regardless of digit width.
    mono: '"SF Mono", ui-monospace, "JetBrains Mono", Menlo, Monaco, Consolas, "Roboto Mono", monospace',
  },
  fontSize: {
    micro: '0.6875rem', // 11px — column headers, footnote
    caption: '0.75rem', // 12px — secondary metadata
    body: '0.875rem', // 14px — default
    bodyStrong: '0.9375rem', // 15px — emphasis
    h6: '1rem',
    h5: '1.125rem',
    h4: '1.25rem',
    h3: '1.5rem',
    h2: '1.875rem',
    h1: '2.25rem',
    display: '3rem',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.15,
    snug: 1.3,
    normal: 1.5,
  },
  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0em',
    wide: '0.02em',
    widest: '0.08em', // section headers / labels
  },
} as const;

export const spacing = {
  px: '1px',
  0: '0',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

export const radius = {
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  full: '9999px',
} as const;

export const borderWidth = {
  hairline: '1px',
  default: '1px',
  strong: '2px',
} as const;

export const elevation = {
  // Z-index slots. Keep ordered: dropdown < popover < dialog < toast.
  z: {
    base: 0,
    raised: 1,
    sticky: 10,
    dropdown: 1000,
    popover: 1100,
    dialog: 1200,
    toast: 1300,
  },
  // Shadows are subtle in dark mode (depth from value, not blur); a touch
  // more pronounced in light mode where there's no luminance contrast.
  shadow: {
    none: 'none',
    sm: '0 1px 2px rgba(0, 0, 0, 0.16)',
    md: '0 4px 12px rgba(0, 0, 0, 0.24)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.32)',
    popover: '0 8px 24px rgba(0, 0, 0, 0.36)',
  },
} as const;

export const focusRing = {
  // Width and offset are mode-independent; the color reads from the
  // semantic `border.focus` token so it adapts per mode and the contrast
  // test enforces ≥3:1 against the adjacent surface in both modes.
  width: '2px',
  offset: '2px',
} as const;

export const motion = {
  duration: {
    instant: '0ms',
    fast: '120ms',
    base: '180ms',
    slow: '260ms',
    flash: '500ms', // market-data tick highlight fade
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
    accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
  },
} as const;

// -----------------------------------------------------------------------------
// Mode bundle + exports.
// -----------------------------------------------------------------------------

export type ColorMode = 'dark' | 'light';

export const colors: Record<ColorMode, ColorTokens> = {
  dark: darkColors,
  light: lightColors,
};

export const tokens = {
  colors,
  typography,
  spacing,
  radius,
  borderWidth,
  elevation,
  focusRing,
  motion,
} as const;

// -----------------------------------------------------------------------------
// Mirror helpers — keep tokens.ts / Tailwind / CSS vars / MUI in lockstep.
// -----------------------------------------------------------------------------

/**
 * Flatten the color tokens for a mode into a CSS-var map.
 *   { 'surface.panel': '#13171f', ... } → { '--ds-color-surface-panel': '#13171f', ... }
 *
 * Consumers (index.css generator, MUI theme, tests) iterate this map and
 * never have to know the nested shape. The Tailwind color object uses the
 * SAME var names so a single class flip (.dark vs not) reassigns the
 * entire palette without rebuilding any class.
 */
export function tokensToCssVarMap(mode: ColorMode): Record<string, string> {
  const out: Record<string, string> = {};
  const c = colors[mode];

  const walk = (prefix: string, obj: Record<string, unknown>) => {
    for (const [key, value] of Object.entries(obj)) {
      // Join with a dash only when there's an existing prefix, otherwise
      // an empty prefix yields a leading dash and the var name becomes
      // `--ds-color--direction-buy` (double dash).
      const segment = kebab(key);
      const cssKey = prefix ? `${prefix}-${segment}` : segment;
      if (typeof value === 'string') {
        out[`--ds-color-${cssKey}`] = value;
      } else if (value && typeof value === 'object') {
        walk(cssKey, value as Record<string, unknown>);
      }
    }
  };
  walk('', c as unknown as Record<string, unknown>);

  // Non-color tokens we want available at the CSS level (focus ring +
  // motion are read directly by the FlashCell / focus utilities).
  out['--ds-focus-ring-width'] = focusRing.width;
  out['--ds-focus-ring-offset'] = focusRing.offset;
  out['--ds-motion-duration-fast'] = motion.duration.fast;
  out['--ds-motion-duration-base'] = motion.duration.base;
  out['--ds-motion-duration-flash'] = motion.duration.flash;
  out['--ds-motion-easing-standard'] = motion.easing.standard;

  return out;
}

/**
 * Tailwind color extension. Returns the SAME nested shape as ColorTokens
 * but with each leaf replaced by `var(--ds-color-…)` so `bg-surface-panel`
 * picks up whichever mode the root `.dark` class is in.
 */
export function tailwindColors() {
  // Build the nested var-reference tree from one mode's shape (the keys
  // are identical across modes — guaranteed by the drift test). The
  // output uses kebab-cased keys so Tailwind utility classes resolve as
  // `bg-direction-buy-muted` (not `bg-direction-buyMuted`), and the tree
  // matches the kebab keys declared in tailwind.config.js verbatim.
  const shape = darkColors;
  const build = (prefix: string, obj: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const segment = kebab(key);
      const cssKey = prefix ? `${prefix}-${segment}` : segment;
      if (typeof value === 'string') {
        out[segment] = `var(--ds-color-${cssKey})`;
      } else if (value && typeof value === 'object') {
        out[segment] = build(cssKey, value as Record<string, unknown>);
      }
    }
    return out;
  };
  return build('', shape as unknown as Record<string, unknown>);
}

/**
 * MUI palette + typography derived from the same tokens. ThemeContext
 * calls this for each mode so the MUI dialogs / buttons / inputs read the
 * same surface/border/text values as Tailwind classes.
 */
export function tokensToMuiPalette(mode: ColorMode) {
  const c = colors[mode];
  return {
    mode,
    primary: {
      main: c.accent.primary,
      light: c.accent.primaryHover,
      dark: c.accent.primaryMuted,
      contrastText: c.text.inverse,
    },
    secondary: {
      // Cool slate. Distinct from accent so MUI buttons that opt into
      // secondary don't fight the primary CTA visually.
      main: palette.slate[500],
      light: '#94a3b8',
      dark: '#475569',
    },
    background: {
      default: c.bg.base,
      paper: c.surface.panel,
    },
    text: {
      primary: c.text.primary,
      secondary: c.text.secondary,
      disabled: c.text.disabled,
    },
    error: { main: c.status.error },
    warning: { main: c.status.warn },
    success: { main: c.status.ok },
    info: { main: c.status.info },
    divider: c.border.subtle,
  };
}

export function tokensToMuiTypography() {
  return {
    fontFamily: typography.fontFamily.sans,
    fontWeightRegular: typography.fontWeight.regular,
    fontWeightMedium: typography.fontWeight.medium,
    fontWeightBold: typography.fontWeight.bold,
    h1: { fontWeight: typography.fontWeight.bold, fontSize: typography.fontSize.h1 },
    h2: { fontWeight: typography.fontWeight.bold, fontSize: typography.fontSize.h2 },
    h3: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h3 },
    h4: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h4 },
    h5: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h5 },
    h6: { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.h6 },
    button: {
      textTransform: 'none' as const,
      fontWeight: typography.fontWeight.medium,
    },
  };
}

function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
